/**
 * placeFilters.ts — Open/closed splitting, broader-type fallback, vibe→type resolution.
 *
 * Extracted from usePlaces.ts (H-203). Pure functions — no React dependencies.
 */

import { inferOpenStatus } from '@/utils/openingHoursInference';
import type { Place } from '@/services/googlePlaces';
import type { CategorySettings } from '@/components/SettingsModal';

export function splitOpenClosed(
  places: Place[],
  timeAvailable: string,
  includeClosedPlaces: boolean,
  transport: string = 'walking'
): { open: Place[]; closed: Place[] } {
  // Metres-per-minute by transport mode (rough fallback when routing data absent)
  const SPEED_MPM: Record<string, number> = { walking: 80, transit: 250, taxi: 500 };

  const isOpenForTimeSlot = (place: Place, slot: string): boolean => {
    if (slot !== '1-2-hrs' && slot !== 'half-day') return true;
    const currentHour = new Date().getHours();

    // Use real routing time if available; otherwise estimate from distance + transport
    let travelMins = 0;
    if (place.routingDurationSeconds) {
      travelMins = Math.round(place.routingDurationSeconds / 60);
    } else if (place.distanceMeters) {
      travelMins = Math.round(place.distanceMeters / (SPEED_MPM[transport] || 80));
    }

    const status = inferOpenStatus(place.currentOpeningHours, place.types, currentHour, travelMins);
    return status.confidence === 'unknown' ? true : status.likelyOpen;
  };

  const open: Place[] = [];
  const closed: Place[] = [];

  places.forEach(place => {
    if (!isOpenForTimeSlot(place, timeAvailable)) {
      closed.push(place);
    } else {
      open.push(place);
    }
  });

  const result = includeClosedPlaces ? [...open, ...closed] : open;
  return { open: result, closed };
}

export function getTypesForVibeFromSettings(vibe: string, categorySettings?: CategorySettings): string[] {
  if (categorySettings) {
    const vibeKey = vibe as keyof CategorySettings;
    return categorySettings[vibeKey] || [];
  }
  return [];
}

export function getBroaderTypes(vibes: string[]): string[] {
  const broaderTypes = new Set<string>();

  const allBroadTypes = [
    'tourist_attraction', 'restaurant', 'cafe', 'bar', 'park',
    'museum', 'shopping_mall', 'movie_theater', 'amusement_park',
    'art_gallery', 'night_club', 'spa', 'zoo', 'aquarium'
  ];

  vibes.forEach(vibe => {
    switch (vibe) {
      case 'hungry':
        ['restaurant', 'cafe', 'bar', 'bakery', 'food_court', 'ice_cream_shop'].forEach(t => broaderTypes.add(t));
        break;
      case 'must-sees':
      case 'popular':
        ['tourist_attraction', 'museum', 'park', 'art_gallery', 'landmark', 'historical_landmark'].forEach(t => broaderTypes.add(t));
        break;
      case 'adventurous':
        ['park', 'amusement_park', 'zoo', 'aquarium', 'tourist_attraction', 'stadium'].forEach(t => broaderTypes.add(t));
        break;
      case 'hidden-gems':
        ['cafe', 'bar', 'book_store', 'art_gallery', 'market', 'antique_store'].forEach(t => broaderTypes.add(t));
        break;
      case 'feeling-lucky':
        allBroadTypes.forEach(t => broaderTypes.add(t));
        break;
      default:
        allBroadTypes.slice(0, 6).forEach(t => broaderTypes.add(t));
    }
  });

  return Array.from(broaderTypes);
}
